// controllers/useQuestionGameController.jsx
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getGameSocket, lobbySocket } from '@api/socket';
import { getPersistentUserId } from '@api/userService';

export function UseQuestionGameController() {
    const { id: lobbyCode } = useParams();
    const persistentId = getPersistentUserId();
    const navigate = useNavigate();
    const socket = useMemo(() => getGameSocket('quiz'), []);

    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [scoreboard, setScoreboard] = useState([]);
    const [isCreator, setIsCreator] = useState(false);
    const [isSpectator, setIsSpectator] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [initialCountdown, setInitialCountdown] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const [gameOver, setGameOver] = useState(false);
    const [showFinalScreen, setShowFinalScreen] = useState(false);
    const [showJoinDialog, setShowJoinDialog] = useState(false);
    const [joinError, setJoinError] = useState('');
    const [forcePassword, setForcePassword] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [revealed, setRevealed] = useState(false);
    const [correct, setCorrect] = useState(null);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(100);

    const offsetRef = useRef(0);
    const timerRef = useRef();
    const countdownRef = useRef();

    const startCountdown = useCallback((startTs, duration) => {
        clearInterval(timerRef.current);
        const tick = () => {
            const nowClient = Date.now() + offsetRef.current;
            const msLeft = startTs + duration - nowClient;
            const secLeft = Math.ceil(msLeft / 1000);
            const progressPercent = Math.max(0, (msLeft / duration) * 100);
            setTimeLeft(secLeft > 0 ? secLeft : 0);
            setProgress(progressPercent);
            if (msLeft <= 0) clearInterval(timerRef.current);
        };
        tick();
        timerRef.current = setInterval(tick, 50);
    }, []);

    const handleReturnToLobby = () => {
        lobbySocket.emit('resetGame', { lobbyCode });
        navigate(`/lobby/${lobbyCode}`);
    };

    const handleJoinDialogSubmit = ({ name, password }) => {
        socket.emit('spectatorJoin', {
            lobbyCode,
            user: { name, persistentId, socketId: socket.id },
            password: password || null
        }, (response) => {
            if (response.success) {
                setShowJoinDialog(false);
                if (response.gameState) {
                    const { gameState } = response;
                    setQuestions(gameState.questions);
                    setCurrentIndex(gameState.questionIndex);
                    setIsSpectator(true);
                    if (gameState.startTimestamp && gameState.duration) {
                        startCountdown(gameState.startTimestamp, gameState.duration);
                    }
                }
            } else {
                setJoinError(response.message || 'Fehler beim Beitreten');
                if (response.requiresPassword) setForcePassword(true);
            }
        });
    };

    useEffect(() => {
        socket.on('gameStarting', ({ questions: qs, startTimestamp, duration, isCreator }) => {
            setQuestions(qs);
            setIsCreator(isCreator);
            setInitialCountdown(true);
            setCountdown(3);
            countdownRef.current = setInterval(() => {
                setCountdown(c => {
                    if (c <= 1) {
                        clearInterval(countdownRef.current);
                        setInitialCountdown(false);
                        socket.emit('questionChange', { lobbyCode, questionIndex: 0 });
                        return 0;
                    }
                    return c - 1;
                });
            }, 1000);
        });

        return () => {
            socket.off('gameStarting');
            clearInterval(countdownRef.current);
        };
    }, [socket, lobbyCode]);

    useEffect(() => {
        socket.emit('joinGame', { lobbyCode });
        socket.emit('reconnectToGame', { lobbyCode, persistentId }, response => {
            if (!response.success) return navigate('/');
            if (response.requiresJoin) {
                setForcePassword(response.requiresPassword || false);
                setShowJoinDialog(true);
                setIsLoading(false);
                return;
            }

            const { serverTime, gameState, scoreboard: sb, reveal } = response;
            offsetRef.current = serverTime - Date.now();
            setScoreboard(sb);
            setQuestions(gameState.questions);
            setIsSpectator(!!gameState.isSpectator);
            if (gameState.hasStarted) {
                setCurrentIndex(gameState.questionIndex);
                startCountdown(gameState.startTimestamp, gameState.duration);
            }
            setIsCreator(gameState.isCreator);
            setIsLoading(false);
        });

        return () => {
            clearInterval(timerRef.current);
            clearInterval(countdownRef.current);
        };
    }, [socket, lobbyCode, persistentId, navigate, startCountdown]);

    useEffect(() => {
        socket.on('questionChanged', ({ questionIndex, startTimestamp, duration, serverTime }) => {
            offsetRef.current = serverTime - Date.now();
            setSelected(null);
            setRevealed(false);
            setCorrect(null);
            setCurrentIndex(questionIndex);
            setDuration(duration / 1000);
            startCountdown(startTimestamp, duration);
        });
        return () => socket.off('questionChanged');
    }, [socket, startCountdown]);

    useEffect(() => {
        const onRevealAnswer = ({ questionIndex, correctAnswer }) => {
            if (questionIndex === currentIndex) {
                setCorrect(correctAnswer);
                setRevealed(true);
            }
        };
        socket.on('scoreboardUpdate', setScoreboard);
        socket.on('revealAnswer', onRevealAnswer);
        return () => {
            socket.off('scoreboardUpdate', setScoreboard);
            socket.off('revealAnswer', onRevealAnswer);
        };
    }, [socket, currentIndex]);

    useEffect(() => {
        socket.on('gameOver', () => setGameOver(true));
        return () => socket.off('gameOver');
    }, [socket]);

    const selectAnswer = (option) => {
        if (timeLeft === 0 || revealed || isSpectator) return;
        setSelected(option);
        socket.emit('submitAnswer', {
            lobbyCode,
            questionIndex: currentIndex,
            answer: option,
            persistentId
        });
    };

    const nextQuestion = () => {
        if (currentIndex < questions.length - 1) {
            socket.emit('questionChange', {
                lobbyCode,
                questionIndex: currentIndex + 1
            });
        }
    };

    const handleLeaveLobby = () => {
        lobbySocket.emit('leaveLobby', { lobbyCode, persistentId }, () => navigate('/'));
    };

    return {
        lobbyCode,
        isLoading,
        questions,
        currentIndex,
        scoreboard,
        isCreator,
        isSpectator,
        timeLeft,
        initialCountdown,
        countdown,
        gameOver,
        showFinalScreen,
        setShowFinalScreen,
        showJoinDialog,
        joinError,
        forcePassword,
        selected,
        revealed,
        correct,
        progress,
        duration,
        handleJoinDialogSubmit,
        selectAnswer,
        nextQuestion,
        handleLeaveLobby,
        handleReturnToLobby
    };
}